import { useState } from 'react';

// A free-text token chip input (add multiple, e.g. "Rosebay" + "Rose Bay").
// Add on Enter or comma; remove via the × or Backspace on an empty box. Case +
// duplicates preserved as authored; only exact-duplicate chips are skipped so
// the same token isn't listed twice.
//
// Extracted verbatim from AccountForm (adAccountMatchTokens) so both the account
// form and the reporting campaign-name filter share one implementation. The
// optional `placeholder` overrides the empty-state hint (subsequent chips always
// prompt "Add another…").
const baseClass =
  'w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';

export function TokenChipInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string[];
  onChange: (tokens: string[]) => void;
  placeholder?: string;
  /**
   * Accessible name for the text box. Worth passing: the placeholder is the
   * only label these boxes have, and it CHANGES to "Add another…" as soon as
   * the first chip lands — so a box with chips in it otherwise announces as
   * unlabelled.
   */
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState('');
  const commit = (raw: string): void => {
    const t = raw.trim();
    if (t === '') return;
    if (!value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setDraft('');
  };
  const emptyPlaceholder = placeholder ?? 'e.g. Rosebay';
  return (
    <div
      className={`${baseClass} flex flex-wrap items-center gap-1.5 min-h-[2.5rem] cursor-text`}
      onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
    >
      {value.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center gap-1 rounded bg-kyck-100 px-2 py-0.5 text-xs text-kyck-900"
        >
          {t}
          <button
            type="button"
            aria-label={`Remove ${t}`}
            className="text-kyck-700 hover:text-kyck-900 focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((_, j) => j !== i));
            }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[8rem] border-0 p-0 text-sm focus:outline-none focus:ring-0"
        aria-label={ariaLabel}
        value={draft}
        placeholder={value.length === 0 ? emptyPlaceholder : 'Add another…'}
        onChange={(e) => {
          // A typed/pasted comma commits the token before it (multi-paste friendly).
          if (e.target.value.includes(',')) {
            const parts = e.target.value.split(',');
            parts.slice(0, -1).forEach(commit);
            setDraft(parts[parts.length - 1] ?? '');
          } else {
            setDraft(e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}
